import ResponderBadge from './ResponderBadge';
import { Link } from 'react-router-dom';
import { getMembershipBadge } from '../utils/getMembershipBadge';
import ListingThumb from './ListingThumb';
import { placeholderFor } from '../utils/listingPlaceholder';

// Counterparty-keyed match card. Used on the main Matches page and (in PR 2)
// the Saved/Hidden secondary pages. The card itself renders a `group`
// produced by `groupMatchesByUser` — see Matches.jsx for the shape contract
// (this function is still inline there pending future extraction). Buttons
// are fully driven by props so each page can supply its own pair (e.g.
// Save/Hide on Matches, Saved/Hide on Saved, Save/Unhide on Hidden).
// `onRowClick` is optional and defaults to no-op; Matches uses it to
// snapshot scroll/filter state into sessionStorage so a "View Match Details"
// round-trip restores cleanly.
const UserGroupCard = ({
  group,
  isSaved,
  primaryButton,
  secondaryButton,
  threadsByKey = {},
  onRowClick = () => {}
}) => {
  const profile = group.counterpartyProfile;
  const initials = (profile?.username || '?')
    .split(' ')
    .map(s => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const tradeCount = profile?.badge || 0;
  const countLabel = group.totalMatchCount === 1
    ? '1 pedal match'
    : `${group.totalMatchCount} pedal matches`;
  // Top-card caption (the "You matched this/these" line). Lives in the
  // top header — NOT inside each sub-group band. Uses total match count
  // across the whole user card to decide singular vs plural.
  const topMatchCaption = group.totalMatchCount === 1
    ? 'You matched this pedal'
    : 'You matched these pedals';

  return (
    <div
      key={group.counterpartyUid}
      className="user-group-card"
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
        marginBottom: '1.5rem',
        overflow: 'hidden',
        width: '100%',
        boxSizing: 'border-box',
        padding: '6px'
      }}
    >
    {/* Inner wrapper clips all sections with a slightly smaller radius,
        leaving the outer white padding as a visible frame */}
    <div style={{ borderRadius: '8px', overflow: 'hidden' }}>
      {/* Header strip — avatar (with overlay badges) + left-aligned text block */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.85rem',
        padding: '1rem 1.25rem',
        borderBottom: '1px solid #f3f4f6'
      }}>
        {/* Avatar with membership + trade-count overlays (matches old card pattern) */}
        <div style={{ position: 'relative', width: '52px', height: '52px', flex: '0 0 auto' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '50%',
            backgroundColor: '#E5E7EB',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden',
            fontFamily: 'Nunito, sans-serif',
            fontWeight: '700', color: '#6B7280', fontSize: '1.05rem'
          }}>
            {profile?.photoURL ? (
              <img
                src={profile.photoURL}
                alt={profile.username}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  if (e.currentTarget.parentElement) {
                    e.currentTarget.parentElement.textContent = initials;
                  }
                }}
              />
            ) : initials}
          </div>
          {/* Membership overlay removed — tier shown via pill badge next to username */}
          {/* Trade count overlay (gold circle at bottom-right of avatar). Render even at 0 so users see "this person has no trades yet." */}
          <div
            style={{
              position: 'absolute',
              bottom: '-3px',
              right: '-3px',
              backgroundColor: 'gold',
              color: '#000',
              fontWeight: 'bold',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              fontSize: '0.7rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #fff',
              boxShadow: '0 0 5px rgba(0,0,0,0.3)'
            }}
            title={`${tradeCount} completed trade${tradeCount === 1 ? '' : 's'}`}
          >
            {tradeCount}
          </div>
        </div>

        {/* Text block — left-aligned with the natural left padding of the card */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: 'Nunito, sans-serif',
              fontWeight: '700', fontSize: '1.1rem', color: '#111827',
              textAlign: 'left'
            }}>
              {profile?.username || 'Unknown'}
            </span>
            {getMembershipBadge(profile?.membershipTier)}
            <ResponderBadge profile={profile} />
          </div>
          <span style={{
            fontFamily: 'Nunito, sans-serif',
            fontSize: '0.95rem', color: '#6B7280', marginTop: '2px',
            textAlign: 'left'
          }}>
            {countLabel}
          </span>
          {/* "You matched this/these pedal(s)" — plain text, no box. */}
          <span style={{
            fontFamily: 'Nunito, sans-serif',
            fontSize: '0.95rem',
            fontWeight: '700',
            color: '#C89F3C',
            marginTop: '5px'
          }}>
            {topMatchCaption}
          </span>
        </div>
      </div>

      {/* Body: sub-groups. Each sub-group is anchored on the SINGLE pedal
          side of a 1-to-many constellation; the many side renders as
          uniform full-width rows beneath, separated by hairline borders.
          The anchor band spans the full card width (gold treatment when
          the anchor is YOUR pedal, neutral when it's theirs) and carries
          a small gold caption identifying who matched what. Per-row
          ownership labels and inner color blocks have been intentionally
          removed for visual cleanliness — the band caption alone
          communicates which side is which. */}
      {group.subGroups.map((sg, sgIdx) => {
        const anchorIsMine = sg.anchorSide === 'mine';
        const anchorPedal = sg.anchorPedal;
        const rowCount = sg.matches.length;
        const rowsAreMine = !anchorIsMine;

        // ABSOLUTE color rule:
        //   MY pedal   → white background, gray dividers
        //   THEIR pedal → blue tint background
        const BLUE_BG = 'rgba(59, 130, 246, 0.10)';
        const BLUE_HOVER_BG = 'rgba(59, 130, 246, 0.18)';
        const BLUE_LABEL = '#1E40AF';
        const WHITE_BG = 'white';
        const WHITE_HOVER_BG = '#F9FAFB';
        const NEUTRAL_LABEL = '#6B7280';

        // Section header labels — singular/plural per side.
        const theirName = profile?.username || 'them';
        const myLabel = rowCount === 1
          ? `Your pedal ${theirName} wants`
          : `Your pedals ${theirName} wants`;
        const theirLabel = rowCount === 1 ? 'Their pedal' : 'Their pedals';
        const anchorSectionLabel = anchorIsMine
          ? `Your pedal ${theirName} wants`
          : 'Their pedal';
        const rowsSectionLabel = rowsAreMine ? myLabel : theirLabel;

        const GOLD_LABEL = '#946F00';

        // Per-element colors: white for mine, blue for theirs.
        // "My pedal" section labels are gold to match the brand tone.
        const anchorBg = anchorIsMine ? WHITE_BG : BLUE_BG;
        const anchorLabelColor = anchorIsMine ? GOLD_LABEL : BLUE_LABEL;
        const rowsLabelColor = rowsAreMine ? GOLD_LABEL : BLUE_LABEL;
        const rowBg = rowsAreMine ? WHITE_BG : BLUE_BG;
        const rowHoverBg = rowsAreMine ? WHITE_HOVER_BG : BLUE_HOVER_BG;

        return (
          <div key={`${sg.anchorSide}-${anchorPedal.id}-${sgIdx}`}>
            {/* Anchor section: section label (left-aligned) above the
                pedal block. Background follows ownership rule. */}
            <div style={{
              backgroundColor: anchorBg,
              borderTop: sgIdx === 0 ? '1px solid #f3f4f6' : '1px solid #E5E7EB'
            }}>
              <div style={{
                padding: '0.9rem 1.25rem 0.3rem 1.25rem',
                fontFamily: 'Nunito, sans-serif',
                fontSize: '0.88rem',
                fontWeight: '700',
                color: anchorLabelColor,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                textAlign: 'left'
              }}>
                {anchorSectionLabel}
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.85rem',
                padding: '0.85rem 1.25rem 0.85rem 1.25rem'
              }}>
                {(anchorPedal?.images?.top || placeholderFor(anchorPedal || {}).url) ? (
                  <ListingThumb
                    src={anchorPedal?.images?.top || placeholderFor(anchorPedal || {}).url}
                    alt={`${anchorPedal.make} ${anchorPedal.model}`}
                    style={{
                      width: '72px', height: '72px',
                      borderRadius: '10px', objectFit: 'cover',
                      flex: '0 0 auto'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '72px', height: '72px',
                    borderRadius: '10px',
                    backgroundColor: '#E5E7EB',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flex: '0 0 auto', color: '#9CA3AF'
                  }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C7 2 4 6 4 10c0 6 5 12 8 12s8-6 8-12c0-4-3-8-8-8z" />
                    </svg>
                  </div>
                )}
                <div style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'flex-start', textAlign: 'left',
                  minWidth: 0, flex: 1, wordBreak: 'break-word'
                }}>
                  <div style={{
                    fontFamily: 'Nunito, sans-serif',
                    fontSize: '1.05rem', fontWeight: '700',
                    color: '#111827', lineHeight: '1.25'
                  }}>
                    {anchorPedal.make}
                  </div>
                  <div style={{
                    fontFamily: 'Nunito, sans-serif',
                    fontSize: '0.9rem', fontWeight: '500',
                    color: '#6B7280', lineHeight: '1.3', marginTop: '2px'
                  }}>
                    {anchorPedal.model}
                  </div>
                  <div style={{
                    fontFamily: 'Nunito, sans-serif',
                    fontSize: '0.9rem', color: '#6B7280', marginTop: '4px'
                  }}>
                    {anchorPedal.condition} · ${anchorPedal.price}
                  </div>
                </div>
              </div>
            </div>

            {/* Rows section header (sits above the row list, in matching bg) */}
            <div style={{
              backgroundColor: rowBg,
              borderTop: '1px solid #E5E7EB',
              padding: '0.9rem 1.25rem 0.3rem 1.25rem',
              fontFamily: 'Nunito, sans-serif',
              fontSize: '0.88rem',
              fontWeight: '700',
              color: rowsLabelColor,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              textAlign: 'left'
            }}>
              {rowsSectionLabel}
            </div>

            {/* Rows beneath the anchor — uniform 72px thumbnail, hairline
                separators between every row. Background follows ownership
                rule. Make and model rendered on their own lines, left-
                aligned, with break-word so long names wrap cleanly within
                the available text column instead of overflowing.
                Perspective-preserving URL: see chat 2026-05-30 on
                MatchDetails URL handling. */}
            {sg.matches.map((m, rowIdx) => {
              const rowPedal = anchorIsMine ? m.matchedPedal : m.myPedal;
              return (
                <Link
                  key={m.matchId}
                  to={`/match/${m.myPedal.id}_${m.matchedPedal.id}`}
                  onClick={onRowClick}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.85rem',
                    padding: '0.85rem 1.25rem',
                    backgroundColor: rowBg,
                    borderTop: rowIdx === 0 ? '1px solid transparent' : '1px solid #E5E7EB',
                    textDecoration: 'none',
                    color: 'inherit',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = rowHoverBg}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = rowBg}
                >
                  {/* Unread match comments (owner correction, 2026-11-01): a red
                      numbered badge on the thumbnail corner, counting only the
                      other member's comments newer than my read marker. The
                      match page clears it once the thread loads. */}
                  <div style={{ position: 'relative', flex: '0 0 auto' }}>
                  {(() => {
                    const unread = threadsByKey[[m.myPedal.id, m.matchedPedal.id].sort().join('_')];
                    return unread > 0 ? (
                      <span aria-label={`${unread} unread comment${unread === 1 ? '' : 's'}`} style={{
                        position: 'absolute', top: '-6px', right: '-6px', minWidth: '20px', height: '20px', padding: '0 6px',
                        borderRadius: '10px', backgroundColor: '#dc2626', color: 'white', fontFamily: 'Nunito, sans-serif',
                        fontSize: '0.72rem', fontWeight: 700, lineHeight: '20px', textAlign: 'center', boxSizing: 'border-box',
                        boxShadow: '0 0 0 2px white', zIndex: 1
                      }}>{unread > 99 ? '99+' : unread}</span>
                    ) : null;
                  })()}
                  {(rowPedal?.images?.top || placeholderFor(rowPedal || {}).url) ? (
                    <ListingThumb
                      src={rowPedal?.images?.top || placeholderFor(rowPedal || {}).url}
                      alt={`${rowPedal.make} ${rowPedal.model}`}
                      style={{
                        width: '72px', height: '72px',
                        borderRadius: '10px', objectFit: 'cover',
                        flex: '0 0 auto'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '72px', height: '72px',
                      borderRadius: '10px',
                      backgroundColor: '#E5E7EB',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flex: '0 0 auto', color: '#9CA3AF'
                    }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C7 2 4 6 4 10c0 6 5 12 8 12s8-6 8-12c0-4-3-8-8-8z" />
                      </svg>
                    </div>
                  )}
                  </div>
                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'flex-start', textAlign: 'left',
                    minWidth: 0, flex: 1, wordBreak: 'break-word'
                  }}>
                    <div style={{
                      fontFamily: 'Nunito, sans-serif',
                      fontSize: '1.05rem', fontWeight: '700',
                      color: '#111827', lineHeight: '1.25'
                    }}>
                      {rowPedal.make}
                    </div>
                    <div style={{
                      fontFamily: 'Nunito, sans-serif',
                      fontSize: '0.9rem', fontWeight: '500',
                      color: '#6B7280', lineHeight: '1.3', marginTop: '2px'
                    }}>
                      {rowPedal.model}
                    </div>
                    <div style={{
                      fontFamily: 'Nunito, sans-serif',
                      fontSize: '0.9rem', color: '#6B7280', marginTop: '4px'
                    }}>
                      {rowPedal.condition} · ${rowPedal.price}
                    </div>
                  </div>
                  {/* The whole row is the link; the cue is a "View Match" label
                      tucked against a heavy chevron, both in the action blue
                      (Jason, 2026-10-20). */}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', flex: '0 0 auto', alignSelf: 'center', marginLeft: '6px', color: '#5A9ECB', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                    View Match
                    <svg width="24" height="24" viewBox="0 0 24 24"
                         fill="none" stroke="#5A9ECB" strokeWidth="3.25" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </span>
                </Link>
              );
            })}
          </div>
        );
      })}

      {/* Footer: bulk save / hide actions */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        padding: '1rem 1.25rem',
        borderTop: '1px solid #f3f4f6'
      }}>
        <button
          onClick={primaryButton.onClick}
          style={{
            flex: 1,
            padding: '0.75rem',
            borderRadius: '9999px',
            border: '1.5px solid #C89F3C',
            backgroundColor: primaryButton.outline ? 'white' : '#C89F3C',
            color: primaryButton.outline ? '#C89F3C' : 'white',
            fontFamily: 'Nunito, sans-serif',
            fontWeight: '700', fontSize: '1rem',
            cursor: 'pointer'
          }}
        >
          {primaryButton.label}
        </button>
        <button
          onClick={secondaryButton.onClick}
          style={{
            flex: 1,
            padding: '0.75rem',
            borderRadius: '9999px',
            border: '1.5px solid #D1D5DB',
            backgroundColor: 'white',
            color: '#374151',
            fontFamily: 'Nunito, sans-serif',
            fontWeight: '700', fontSize: '1rem',
            cursor: 'pointer'
          }}
        >
          {secondaryButton.label}
        </button>
      </div>
    </div>{/* end inner wrapper */}
    </div>
  );
};

export default UserGroupCard;
